<template>
    <flex-container>
        <flex-container
            :gap="Size.xl"
            align-items="center"
            no-wrap
        >
            <flex-container
                :gap="Size.sm"
                align-items="center"
                size="auto"
            >
                <back-button :to="{ name: 'lead-list' }" />
                <user-avatar
                    :placeholder="contact?.name.abbreviation"
                    :size="IconSize.xl"
                    :color="'red-500'"
                />
                <h2>
                    {{ contact?.name.fullName }}
                </h2>
            </flex-container>

            <flex-container v-if="contact" size="auto">
                <contact-card
                    :contact="contact as Contact"
                    @refresh="reload()"
                />
            </flex-container>
        </flex-container>

        <inline-tabs selected-name="flow">
            <template #labels>
                <inline-tab-label name="sent-emails">
                    E-mails
                </inline-tab-label>
                <inline-tab-label name="objects">
                    Objecten
                </inline-tab-label>
                <inline-tab-label name="appointments">
                    Afspraken
                </inline-tab-label>
                <inline-tab-label name="tasks">
                    Taken
                </inline-tab-label>
                <inline-tab-label name="realworks">
                    Realworks
                </inline-tab-label>
                <inline-tab-label name="flow">
                    Flows
                </inline-tab-label>
            </template>

            <template #panels>
                <contact-sent-email-panel :sent-email-collection="sentEmailCollection" />
                <contact-object-panel :wonen-objecten="wonenObjecten" />
                <contact-appointment-panel :appointment-collection="appointmentCollection" />
                <contact-task-panel :task-collection="taskCollection" />
                <contact-realworks-panel :contact-id="contactId" />
                <contact-flow-panel
                    :lead-collection="leadCollection"
                    :sent-email-collection="sentEmailCollection"
                    :contact-id="contactId"
                />
            </template>
        </inline-tabs>
    </flex-container>
</template>

<script setup lang="ts">
    import BackButton from '@/shared/components/BackButton.vue';
    import { useRouter } from 'vue-router';
    import { computed, onMounted, ref, type Ref } from 'vue';
    import { Appointment, Contact, ContactId, Lead, SentFlowEmail, Task } from '@keyvue/one-api-client';
    import ContactCard from '@/lead/contact/components/ContactCard.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import ContactFlowPanel from '@/lead/contact/components/ContactFlowPanel.vue';
    import { IconSize, Size } from '@keyvue/one-ui';
    import ContactAppointmentPanel from '@/lead/contact/components/ContactAppointmentPanel.vue';
    import ContactTaskPanel from '@/lead/contact/components/ContactTaskPanel.vue';
    import { RealworksAPI, RealworksWonenObject } from '@keyvue/one-api-client-legacy';
    import ContactObjectPanel from '@/lead/contact/components/ContactObjectPanel.vue';
    import ContactSentEmailPanel from '@/lead/contact/components/ContactSentEmailPanel.vue';
    import ContactRealworksPanel from '@/lead/contact/components/ContactRealworksPanel.vue';

    const router = useRouter();

    const contactId = computed(() => new ContactId(router.currentRoute.value.params?.contactId as string));

    const { client, getLazyResourceCollection } = useClient();

    const contact = ref<Contact | null>(null);
    const leadCollection = getLazyResourceCollection(Lead, {
        primaryContact: contactId.value,
    });
    const appointmentCollection = getLazyResourceCollection(Appointment, {
        'lead.primaryContact': contactId.value,
    });
    const taskCollection = getLazyResourceCollection(Task, {
        'lead.primaryContact': contactId.value,
    });
    const sentEmailCollection = getLazyResourceCollection(SentFlowEmail, {
        'lead.primaryContact': contactId.value,
    });
    const wonenObjecten = ref<RealworksWonenObject[]>([]) as Ref<RealworksWonenObject[]>;

    onMounted(async () => {
        await load();
    });

    async function load() {
        contact.value = await client.get(Contact, contactId.value);
        await leadCollection.load({
            primaryContact: contactId.value,
        });
        await appointmentCollection.load({
            'lead.primaryContact': contactId.value,
        });
        await taskCollection.load({
            'lead.primaryContact': contactId.value,
        });
        await sentEmailCollection.load({
            'lead.primaryContact': contactId.value,
        });
        wonenObjecten.value = await RealworksAPI.getRealworksWonenObjecten(contactId.value.value);
    }

    async function reload() {
        await new Promise((resolve) => {
            setTimeout(resolve, 2000);
        });
        await load();
    }
</script>
